/**
 * Cash & Bank — where the company's money is, who holds it, and how it moved.
 *
 * Each balance is the account's opening balance plus every movement that
 * landed in it: the account a payment named, else its location's default for
 * the method. Every source — customer payments, remittance, loans and their
 * repayments, expenses, rider payouts and cash returns, investor payouts,
 * transfers — reaches exactly one account, read from the record that caused
 * it, so nothing is posted twice. Money no account claims is shown as
 * unassigned, by source and location, rather than left out.
 *
 * Kept apart and never in a balance: cash a rider holds until they hand it
 * over, bank payments still awaiting verification, and deposits that have
 * left a cash account but not been confirmed at the bank ("in transit").
 * Banking cash is a transfer, never revenue; a transfer entered in error is
 * voided with a reason and stays on the list.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  History,
  Landmark,
  Lock,
  MapPin,
  PencilLine,
  RefreshCw,
  ShieldAlert,
  SlidersHorizontal,
} from 'lucide-react';
import {
  MONEY_ACCOUNT_KINDS,
  MONEY_ACCOUNT_KIND_LABELS,
  type CashBankAccountView,
  type CashBankDashboard,
  type CashBankPeriodFigures,
  type MoneyAccountKind,
  type MoneyAccountView,
  type MoneyTransferView,
} from '@cenox/shared';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { GeoScopeFields } from '../../components/GeoScope';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useAreaFilter, useLocationReady, useWorkingLocation } from '../../lib/locationContext';
import { money, todayIn } from '../../lib/format';
import { addAmounts, subtractAmounts } from '@cenox/shared';
import { newSubmissionKey } from '../loans/data';
import { CashFlowCard, JournalModal, StatementsCard } from './CashReports';
import { TransactionHistory } from './TransactionHistory';
import { AwaitingConfirmation, Collections, ConfirmOrDecline, MyCash, MyCashPage, Position, TRANSFER_KIND_LABEL } from './Custody';
import {
  useAccountMovements,
  useCashBankDashboard,
  useCreateAccount,
  useMemberOptions,
  useRecordTransfer,
  useStatements,
  useTransfers,
  useUpdateAccount,
  useUploadEvidence,
  useVoidTransfer,
  type MemberOption,
} from './data';

import { BALANCE_UNAVAILABLE, BALANCE_UNAVAILABLE_WHY, dayLabel, SOURCE_LABELS } from './labels';

export { SOURCE_LABELS };

const firstOfMonth = (iso: string): string => `${iso.slice(0, 7)}-01`;

export function MoneyAccountsPage(): ReactNode {
  const { can } = useAuth();
  // A worker who hands over cash but does not read the business's figures sees only their own.
  return can('reports:read') ? <CashBankPage /> : <MyCashPage />;
}

function CashBankPage(): ReactNode {
  const { business, can } = useAuth();
  const timezone = business?.timezone ?? 'UTC';
  const today = todayIn(timezone);
  const [from, setFrom] = useState(firstOfMonth(today));
  const [to, setTo] = useState(today);
  const [accountId, setAccountId] = useState('');
  const [holderUserId, setHolder] = useState('');
  const area = useAreaFilter();
  const mayManage = can('cash:reconcile');
  const mayDeposit = mayManage || can('cash:deposit');
  const filters = useMemo(
    () => ({
      from,
      to: to < from ? from : to,
      ...area,
      ...(accountId ? { accountId } : {}),
      ...(holderUserId ? { holderUserId } : {}),
    }),
    [from, to, area, accountId, holderUserId],
  );
  const dashboard = useCashBankDashboard(filters, useLocationReady());
  const transfers = useTransfers();
  const members = useMemberOptions(mayManage);
  const [creating, setCreating] = useState(false);
  // Moving money; a panel's Record button picks the kind of account it goes to.
  const [moving, setMoving] = useState<{ toKind?: 'cash' | 'bank' } | null>(null);
  const { locations } = useWorkingLocation();
  // The history reads the page's filters; its own location picker narrows them further.
  const historyScope = useMemo(
    () => ({ ...area, ...(accountId ? { accountId } : {}), ...(holderUserId ? { holderUserId } : {}) }),
    [area, accountId, holderUserId],
  );
  const [viewing, setViewing] = useState<CashBankAccountView | null>(null);
  const [journal, setJournal] = useState(false);
  const [opening, setOpening] = useState<MoneyAccountView | null>(null);
  const [moreFilters, setMoreFilters] = useState(false);
  const queryClient = useQueryClient();

  // Started now, not when the dashboard has answered: each section loads on its own.
  useStatements();
  const data = dashboard.data;
  const currency = data?.currencyCode ?? business?.currencyCode ?? 'NGN';
  const m = (v: string) => money(v, currency);
  const live = (data?.accounts ?? []).filter((a) => !a.archived);
  const holders = [
    ...new Map((data?.accounts ?? []).filter((a) => a.holderUserId).map((a) => [a.holderUserId!, a.holderName ?? 'Worker'])).entries(),
  ];

  const unavailable = (data?.accounts ?? []).filter((a) => a.balance === null);
  const onRecordCash = mayDeposit && live.length >= 2 ? () => setMoving({ toKind: 'cash' }) : undefined;
  const onRecordBank = mayDeposit && live.length >= 2 ? () => setMoving({ toKind: 'bank' }) : undefined;

  return (
    <div className="space-y-6">
      <section className="card space-y-5 p-4 sm:p-6" data-testid="cash-bank-overview">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Cash &amp; Bank</h1>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <DateRangeControl from={from} to={filters.to} today={today} onFrom={setFrom} onTo={setTo} />
            <LocationControl />
            <ToolbarIcon label="More filters" pressed={moreFilters} onClick={() => setMoreFilters((v) => !v)}>
              <SlidersHorizontal aria-hidden className="h-4 w-4" />
            </ToolbarIcon>
            <ToolbarIcon label="Refresh" onClick={() => void queryClient.invalidateQueries({ queryKey: ['money'] })}>
              <RefreshCw aria-hidden className={cn('h-4 w-4', dashboard.isFetching && 'animate-spin')} />
            </ToolbarIcon>
          </div>
        </header>

        {moreFilters ? (
          <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="cash-bank-filters">
            <GeoScopeFields bare idPrefix="cash-bank-scope" />
            <Select
              label="Account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              options={[{ value: '', label: 'All accounts' }, ...(data?.accounts ?? []).map((a) => ({ value: a.id, label: a.name }))]}
            />
            <Select
              label="Worker"
              value={holderUserId}
              onChange={(e) => setHolder(e.target.value)}
              options={[{ value: '', label: 'All workers' }, ...holders.map(([id, name]) => ({ value: id, label: name }))]}
            />
          </div>
        ) : null}

        {dashboard.isError ? (
          <ErrorState message="Could not load Cash & Bank." onRetry={() => void dashboard.refetch()} />
        ) : null}
        {data ? (
          <>
            {unavailable.length > 0 ? <AccessBanner count={unavailable.length} /> : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="cash-bank-totals">
              <TotalCard data={data} kind="cash" />
              <TotalCard data={data} kind="bank" />
            </div>
            <PeriodSummary data={data} />
          </>
        ) : dashboard.isError ? null : <TableSkeleton rows={3} columns={5} />}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Where the company&apos;s money is and who holds it. Only money that moved — revenue is not here.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setJournal(true)}>Journal</Button>
          {mayManage ? <Button variant="secondary" onClick={() => setCreating(true)}>Add account</Button> : null}
          {mayDeposit ? <Button onClick={() => setMoving({})} disabled={live.length < 2}>Move money</Button> : null}
        </div>
      </div>

      {data ? (
        <>
          <Attention data={data} />
          <Accounts
            accounts={data.accounts}
            openingAsOf={data.summary.openingAsOf}
            from={data.from}
            to={data.to}
            onView={setViewing}
            onOpening={mayManage ? setOpening : undefined}
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CashPanel data={data} onRecord={onRecordCash} />
            <BankPanel data={data} onRecord={onRecordBank} />
          </div>
        </>
      ) : null}
      <MyCash standalone={false} />
      {data ? <AwaitingConfirmation transfers={data.awaitingConfirmation} currency={currency} today={today} /> : null}

      <TransactionHistory
        from={filters.from}
        to={filters.to}
        scope={historyScope}
        currency={currency}
        timezone={timezone}
        locations={locations}
      />

      {data ? (
        <>
          <Unassigned data={data} />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Position data={data} />
            <Collections data={data} />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card>
              <h2 className="text-sm font-semibold text-slate-900">Held with riders — not in any account</h2>
              <p className="mt-0.5 text-xs text-slate-500">Rider money stays separate until the rider hands it over.</p>
              <dl className="mt-2 space-y-1 text-sm" data-testid="rider-held">
                <div className="flex justify-between gap-3">
                  <dt>Confirmed as received by riders, not yet returned</dt>
                  <dd className="whitespace-nowrap font-semibold tabular-nums">{m(data.riderHeld.confirmed)}</dd>
                </div>
                {data.riderHeld.riders.slice(0, 8).map((r) => (
                  <div key={r.riderId} className="flex justify-between gap-3 pl-3 text-slate-600">
                    <dt>{r.riderName}</dt>
                    <dd className="whitespace-nowrap tabular-nums">{m(r.amount)}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-3 border-t border-slate-100 pt-1 text-amber-800">
                  <dt>Recorded as with riders in this period, nobody confirmed (Cash to Rider, synced cash)</dt>
                  <dd className="whitespace-nowrap tabular-nums">{m(data.riderHeld.unconfirmed)}</dd>
                </div>
              </dl>
              {(data.riderHeld.unconfirmedByRider ?? []).length > 0 ? (
                <div className="-mx-3 mt-2 overflow-x-auto px-3">
                  <table className="w-full min-w-[28rem] text-sm" data-testid="rider-held-by-rider">
                    <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="py-1 pr-3 font-medium">Rider</th>
                        <th className="py-1 pr-3 text-right font-medium">Took from customers</th>
                        <th className="py-1 pr-3 text-right font-medium">Their pay</th>
                        <th className="py-1 text-right font-medium">Company&apos;s part owed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.riderHeld.unconfirmedByRider.map((r) => (
                        <tr key={`${r.riderId ?? ''}-${r.riderName}`}>
                          <td className="py-1 pr-3">{r.riderName} <span className="text-xs text-slate-400">· {r.rides}</span></td>
                          <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">{m(r.held)}</td>
                          <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">{m(r.riderShare)}</td>
                          <td className="whitespace-nowrap py-1 text-right font-medium tabular-nums">{m(r.companyShare)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1 text-xs text-slate-500">
                    Not the company&apos;s cash yet: the company&apos;s part is owed by the rider until it is taken from their earnings or handed over.
                  </p>
                </div>
              ) : null}
            </Card>
          </div>

        </>
      ) : null}
      <CashFlowCard from={filters.from} to={filters.to} area={area} currency={currency} />
      {data ? <StatementsCard accounts={data.accounts} currency={currency} mayManage={mayManage} today={today} /> : null}

      <Transfers transfers={transfers.data ?? []} currency={currency} mayManage={mayManage} today={today} />
      {journal ? <JournalModal filters={filters} currency={currency} onClose={() => setJournal(false)} /> : null}
      {opening ? <OpeningModal account={opening} today={today} onClose={() => setOpening(null)} /> : null}

      {creating ? <CreateAccountModal members={members.data ?? []} onClose={() => setCreating(false)} /> : null}
      {moving ? <TransferModal accounts={live} mayManage={mayManage} toKind={moving.toKind} onClose={() => setMoving(null)} /> : null}
      {viewing && data ? (
        <MovementsModal account={viewing} from={data.from} to={data.to} openingAsOf={data.summary.openingAsOf} onClose={() => setViewing(null)} />
      ) : null}
    </div>
  );
}

interface PanelRow {
  key: string;
  name: string;
  amount: string;
  /** Under the name: who holds it, or why it is not in an account. */
  detail?: string | undefined;
  warn?: boolean | undefined;
}

/** A share of the panel's total, or a dash where a share would mislead (nothing held, or a negative). */
function shareOf(amount: string, total: string): string {
  const a = Number(amount);
  const t = Number(total);
  if (!(t > 0) || a <= 0) return '—';
  return `${((a / t) * 100).toFixed(1)}%`;
}

/**
 * Physical cash by location. Each location is its cash accounts, the cash
 * recorded there that no account claims yet, and cash it sent that the
 * receiver has not confirmed — still counted where it left, never in the bank
 * until it arrives. The panel's total is the sum of these rows, nothing more.
 */
export function cashPanelRows(data: CashBankDashboard): PanelRow[] {
  const m = (v: string) => money(v, data.currencyCode);
  if (data.cashByLocation.length > 0) {
    return data.cashByLocation.map((c) => {
      const notes = [
        ...c.holders.filter((h) => h.holderName).map((h) => `${h.holderName}: ${m(h.balance)}`),
        ...(c.unassigned !== '0.00' ? [`${m(c.unassigned)} not in an account yet`] : []),
        ...(c.sentAwaitingConfirmation !== '0.00' ? [`${m(c.sentAwaitingConfirmation)} sent, awaiting confirmation`] : []),
      ];
      return {
        key: c.branchId ?? 'none',
        name: c.branchName ?? 'Whole business',
        amount: c.total,
        detail: notes.join(' · ') || undefined,
        warn: c.unassigned !== '0.00' || c.sentAwaitingConfirmation !== '0.00',
      };
    });
  }
  // Narrowed to an account or a worker: their cash accounts, by location.
  const by = new Map<string, PanelRow>();
  for (const a of data.accounts.filter((x) => x.kind === 'cash' && x.balance !== null)) {
    const key = a.branchId ?? 'none';
    const row = by.get(key) ?? { key, name: a.branchName ?? 'Whole business', amount: '0.00' };
    row.amount = addAmounts(row.amount, a.balance!);
    by.set(key, row);
  }
  return [...by.values()];
}

/** Bank and wallet money by account, plus any recorded as paid electronically that no account claims. */
export function bankPanelRows(data: CashBankDashboard): PanelRow[] {
  const rows: PanelRow[] = data.accounts
    .filter((a) => a.kind !== 'cash' && a.balance !== null && !(a.archived && a.balance === '0.00'))
    .map((a) => ({
      key: a.id,
      name: a.name,
      amount: a.balance!,
      detail: [MONEY_ACCOUNT_KIND_LABELS[a.kind], a.branchName, a.archived ? 'closed' : null].filter(Boolean).join(' · '),
    }));
  if (data.unassigned.bank !== '0.00') {
    rows.push({
      key: 'unassigned',
      name: 'Not in a bank account yet',
      amount: data.unassigned.bank,
      detail: 'Recorded as paid electronically; no account claims it, so no bank has confirmed it',
      warn: true,
    });
  }
  return rows;
}

/** The accounts of one kind whose full balance is not available with the viewer's access. */
function unavailableOf(data: CashBankDashboard, cash: boolean): CashBankAccountView[] {
  return data.accounts.filter((a) => a.balance === null && (a.kind === 'cash') === cash);
}

/**
 * Shown to a member limited to some locations when an account holds money
 * recorded at a location they may not see. Its balance is not shown anywhere
 * on the page, and every total leaves it out.
 */
function AccessBanner({ count }: { count: number }): ReactNode {
  return (
    <div role="status" className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="access-banner">
      <ShieldAlert aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <div className="text-sm">
        <p className="font-semibold text-amber-900">Some business balances are unavailable</p>
        <p className="mt-0.5 text-amber-800">
          Some financial records affecting {count === 1 ? 'one account' : `${count} accounts`} belong to locations you
          don&apos;t have access to. Their balances are not shown and are left out of the totals below.
        </p>
      </div>
    </div>
  );
}

const PANEL_TONES = {
  cash: {
    bar: 'bg-emerald-500',
    card: 'border-green-200 bg-green-50',
    icon: 'text-emerald-600',
  },
  bank: {
    bar: 'bg-brand-600',
    card: 'border-blue-200 bg-blue-50',
    icon: 'text-brand-600',
  },
} as const;

/** "31 Oct", or "31 Oct 2025" when the year differs from the period's end. */
function shortDay(iso: string, yearOf: string): string {
  const label = dayLabel(iso);
  return iso.slice(0, 4) === yearOf.slice(0, 4) ? label.replace(/ \d{4}$/, '') : label;
}

const CONTROL = 'inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 text-[15px] font-medium text-slate-800 shadow-sm';

/** The period, shown as one range; opening it gives the two dates. */
function DateRangeControl({
  from,
  to,
  today,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  today: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative w-full sm:w-auto">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`Dates: ${dayLabel(from)} to ${dayLabel(to)}`}
        onClick={() => setOpen((v) => !v)}
        className={cn(CONTROL, 'w-full justify-between hover:bg-slate-50 sm:w-auto')}
        data-testid="date-range"
      >
        <span className="inline-flex items-center gap-2">
          <CalendarDays aria-hidden className="h-4 w-4 text-slate-500" />
          <span className="whitespace-nowrap">{dayLabel(from)} – {dayLabel(to)}</span>
        </span>
        <ChevronDown aria-hidden className="h-4 w-4 text-slate-500" />
      </button>
      {open ? (
        <>
          <button type="button" aria-label="Close dates" className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-2 grid w-72 gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
            <Input label="From" type="date" value={from} max={today} onChange={(e) => e.target.value && onFrom(e.target.value)} />
            <Input label="To (balances as of)" type="date" value={to} max={today} onChange={(e) => e.target.value && onTo(e.target.value)} />
            <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** The working location, as the sidebar sets it; country and state are under More filters. */
function LocationControl(): ReactNode {
  const { selection, scopedLocations, canAggregate, setBranchId } = useWorkingLocation();
  const options = [
    ...(canAggregate ? [{ value: '', label: 'All Locations' }] : []),
    ...(scopedLocations ?? []).map((l) => ({ value: l.id, label: l.name })),
  ];
  return (
    <label className={cn(CONTROL, 'relative min-w-0 flex-1 pr-9 sm:w-56 sm:flex-none')}>
      <MapPin aria-hidden className="h-4 w-4 shrink-0 text-slate-500" />
      <span className="sr-only">Location</span>
      <select
        className="w-full cursor-pointer appearance-none truncate bg-transparent outline-none disabled:cursor-not-allowed disabled:text-slate-500"
        value={selection?.branchId ?? ''}
        disabled={!canAggregate && (scopedLocations ?? []).length <= 1}
        onChange={(e) => setBranchId?.(e.target.value || null)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown aria-hidden className="pointer-events-none absolute right-3 h-4 w-4 text-slate-500" />
    </label>
  );
}

function ToolbarIcon({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(CONTROL, 'w-11 justify-center px-0 text-slate-600 hover:bg-slate-50', pressed && 'border-brand-300 bg-brand-50 text-brand-700')}
    >
      {children}
    </button>
  );
}

/** Total cash or total bank: the closing balance on the period's last day, nothing else. */
function TotalCard({ data, kind }: { data: CashBankDashboard; kind: 'cash' | 'bank' }): ReactNode {
  const rows = kind === 'cash' ? cashPanelRows(data) : bankPanelRows(data);
  const total = rows.reduce((t, r) => addAmounts(t, r.amount), '0.00');
  const excluded = unavailableOf(data, kind === 'cash');
  const Icon = kind === 'cash' ? Banknote : Landmark;
  const testId = `${kind}-panel`;
  return (
    <div className={cn('flex items-center gap-4 rounded-xl border px-5 py-5 sm:gap-6 sm:px-8 sm:py-6', PANEL_TONES[kind].card)} data-testid={`${kind}-total`}>
      <Icon aria-hidden strokeWidth={1.75} className={cn('h-11 w-11 shrink-0 sm:h-16 sm:w-16', PANEL_TONES[kind].icon)} />
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-slate-800">{kind === 'cash' ? 'Total Cash' : 'Total Bank'}</h2>
        <p className="overflow-x-auto whitespace-nowrap text-3xl font-extrabold sm:text-[2.125rem] tracking-tight tabular-nums text-slate-900" data-testid={`${testId}-total`}>
          {money(total, data.currencyCode)}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">Closing balance as of {dayLabel(data.asOf)}</p>
        {excluded.length > 0 ? (
          <p
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
            data-testid={`${kind}-total-partial`}
          >
            <Lock aria-hidden className="h-3.5 w-3.5" />
            Partial total — {excluded.length === 1 ? '1 account' : `${excluded.length} accounts`} left out
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Where a total is held: by location for cash, by account for bank, each with its share. */
function MoneyPanel({
  kind,
  title,
  testId,
  rows,
  firstColumn,
  currency,
  recordLabel,
  onRecord,
  excluded,
  footer,
}: {
  kind: keyof typeof PANEL_TONES;
  title: string;
  testId: string;
  rows: PanelRow[];
  firstColumn: string;
  currency: string;
  recordLabel: string;
  onRecord?: (() => void) | undefined;
  /** Accounts left out of the total because their full balance is not available. */
  excluded: CashBankAccountView[];
  footer?: ReactNode;
}): ReactNode {
  const m = (v: string) => money(v, currency);
  const total = rows.reduce((t, r) => addAmounts(t, r.amount), '0.00');
  const Icon = kind === 'cash' ? Banknote : Landmark;
  return (
    <Card>
      <div data-testid={testId}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900">
            <Icon aria-hidden className={cn('h-5 w-5', PANEL_TONES[kind].icon)} />
            {title}
          </h2>
          {onRecord ? <Button variant="secondary" size="sm" className="shrink-0 whitespace-nowrap" onClick={onRecord}>{recordLabel}</Button> : null}
        </div>
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Nothing held here yet.</p>
        ) : (
          <div className="-mx-3 mt-3 overflow-x-auto px-3">
            <table className="w-full min-w-[20rem] text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="pb-1 pr-3 font-medium">{firstColumn}</th>
                  <th className="pb-1 pr-3 text-right font-medium">Amount</th>
                  <th className="pb-1 text-right font-medium">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td className="py-2 pr-3">
                      <span className={cn('font-medium', r.warn ? 'text-amber-800' : 'text-slate-800')}>{r.name}</span>
                      {r.detail ? <span className="block text-xs text-slate-500">{r.detail}</span> : null}
                    </td>
                    <td className={cn('whitespace-nowrap py-2 pr-3 text-right tabular-nums', Number(r.amount) < 0 ? 'text-rose-700' : 'text-slate-800')}>{m(r.amount)}</td>
                    <td className="whitespace-nowrap py-2 text-right text-xs tabular-nums text-slate-500">{shareOf(r.amount, total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {excluded.length > 0 ? (
          <p className="mt-3 flex gap-1.5 text-xs text-slate-500" data-testid={`${testId}-unavailable`}>
            <Lock aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Not included: {excluded.map((a) => a.name).join(', ')}. {BALANCE_UNAVAILABLE_WHY}</span>
          </p>
        ) : null}
        {footer}
      </div>
    </Card>
  );
}

function CashPanel({ data, onRecord }: { data: CashBankDashboard; onRecord?: (() => void) | undefined }): ReactNode {
  return (
    <MoneyPanel
      kind="cash"
      title="Cash by location"
      testId="cash-panel"
      rows={cashPanelRows(data)}
      firstColumn="Location"
      currency={data.currencyCode}
      recordLabel="Record cash"
      onRecord={onRecord}
      excluded={unavailableOf(data, true)}
    />
  );
}

function BankPanel({ data, onRecord }: { data: CashBankDashboard; onRecord?: (() => void) | undefined }): ReactNode {
  return (
    <MoneyPanel
      kind="bank"
      title="Bank by account"
      testId="bank-panel"
      rows={bankPanelRows(data)}
      firstColumn="Account"
      currency={data.currencyCode}
      recordLabel="Record bank"
      onRecord={onRecord}
      excluded={unavailableOf(data, false)}
      footer={
        data.totals.depositsInTransit !== '0.00' ? (
          <p className="mt-3 text-xs text-amber-800">
            {money(data.totals.depositsInTransit, data.currencyCode)} is on its way from cash and not confirmed at the bank:
            counted in cash where it left, not here.
          </p>
        ) : null
      }
    />
  );
}

/**
 * How cash and bank moved over the chosen dates: what each held the day
 * before they began, what came in and went out during them, what moved between
 * the company's own accounts, and what each held at their end — the totals
 * above. The opening and closing are each worked out on their own, so any
 * amount the movements do not explain is shown, never hidden.
 */
export function PeriodSummary({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  const { openingAsOf, cash, bank } = data.summary;
  const net = (f: CashBankPeriodFigures) => subtractAmounts(f.transfersIn, f.transfersOut);
  // Moving money between cash and bank needs its own column to add up; moving it within cash, or within bank, nets to nothing.
  const showTransfers = [cash, bank].some((f) => net(f) !== '0.00');
  const excluded = data.accounts.filter((a) => a.balance === null).length;
  const rows: Array<['cash' | 'bank', string, CashBankPeriodFigures]> = [['cash', 'Cash', cash], ['bank', 'Bank', bank]];
  const openingWhen = `as of ${shortDay(openingAsOf, data.to)}`;
  const closingWhen = `as of ${shortDay(data.to, data.to)}`;
  const icon = (key: 'cash' | 'bank') =>
    key === 'cash' ? (
      <Banknote aria-hidden className={cn('h-6 w-6', PANEL_TONES.cash.icon)} />
    ) : (
      <Landmark aria-hidden className={cn('h-6 w-6', PANEL_TONES.bank.icon)} />
    );
  const head = (label: string, when?: string) => (
    <th className="px-5 py-3 text-right align-bottom font-semibold text-slate-900">
      <span className="block">{label}</span>
      {when ? <span className="block text-sm font-normal text-slate-500">({when})</span> : null}
    </th>
  );
  return (
    <div>
      <div className="overflow-hidden rounded-xl border border-slate-200">
        {/* Phones: one block per row of the table, closing last. */}
        <div className="divide-y divide-slate-100 sm:hidden" data-testid="period-summary-mobile">
          {rows.map(([key, label, f]) => (
            <dl key={key} className="space-y-1.5 px-4 py-4 text-sm">
              <dt className="flex items-center gap-2 font-semibold text-slate-900">{icon(key)}{label}</dt>
              <FigureLine label={`Opening (${openingWhen})`} value={m(f.opening)} />
              <FigureLine label="Money In" value={m(f.moneyIn)} />
              <FigureLine label="Money Out" value={m(f.moneyOut)} />
              {showTransfers ? <FigureLine label="Own transfers" value={m(net(f))} /> : null}
              <FigureLine label={`Closing (${closingWhen})`} value={m(f.closing)} strong />
            </dl>
          ))}
        </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[36rem] text-base" data-testid="period-summary">
            <thead className="border-b border-slate-200 bg-slate-100/80 text-left">
              <tr>
                <th className="px-5 py-3 align-bottom font-semibold text-slate-900">Summary</th>
                {head('Opening', openingWhen)}
                {head('Money In')}
                {head('Money Out')}
                {showTransfers ? head('Own transfers', 'between your accounts') : null}
                {head('Closing', closingWhen)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map(([key, label, f]) => (
                <tr key={key} data-testid={`${key}-period`}>
                  <th scope="row" className="px-5 py-4 text-left font-semibold text-slate-900">
                    <span className="inline-flex items-center gap-3">
                      {icon(key)}
                      {label}
                      {unavailableOf(data, key === 'cash').length > 0 ? (
                        <span title="Partial: accounts whose full balance is not available are left out" className="inline-flex text-amber-700">
                          <Lock aria-hidden className="h-4 w-4" />
                          <span className="sr-only">(partial)</span>
                        </span>
                      ) : null}
                    </span>
                  </th>
                  <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-800" data-testid={`${key}-period-opening`}>{m(f.opening)}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-800" data-testid={`${key}-period-in`}>{m(f.moneyIn)}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-800" data-testid={`${key}-period-out`}>{m(f.moneyOut)}</td>
                  {showTransfers ? (
                    <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-800" data-testid={`${key}-period-transfers`}>{m(net(f))}</td>
                  ) : null}
                  <td className="whitespace-nowrap px-5 py-4 text-right text-[1.1875rem] font-bold tabular-nums text-slate-900" data-testid={`${key}-period-closing`}>{m(f.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {excluded > 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-800" data-testid="period-summary-partial">
          <Lock aria-hidden className="h-3.5 w-3.5 shrink-0" />
          Partial: leaves out {excluded === 1 ? '1 account' : `${excluded} accounts`} whose full balance is not available with
          your access.
        </p>
      ) : null}
      {rows.map(([key, label, f]) =>
        f.difference !== '0.00' ? (
          <p key={key} role="alert" className="mt-2 text-sm text-rose-700" data-testid={`${key}-period-difference`}>
            {label}: the movements do not add up to the closing balance — {m(f.difference)} is not explained by them. The
            balances shown are each worked out from the records; please report this so it can be looked into.
          </p>
        ) : null,
      )}
    </div>
  );
}

/** Money recorded but not counted as funds, and deposits waiting at the bank. */
function Attention({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  const items: Array<[string, string]> = [];
  if (data.deposits.pending.count > 0) {
    items.push(['Deposits waiting to be confirmed at the bank', `${data.deposits.pending.count} · ${m(data.deposits.pending.amount)}`]);
  }
  if (data.unverified.count > 0) {
    items.push(['Bank payments awaiting verification (not counted)', `${data.unverified.count} · ${m(data.unverified.amount)}`]);
  }
  if (data.unpaidExpenses.count > 0) {
    items.push([
      'Expenses recorded, not paid yet (no money moved)',
      `${data.unpaidExpenses.count} · ${m(data.unpaidExpenses.amount)}${data.unpaidExpenses.due.count > 0 ? ` (${data.unpaidExpenses.due.count} due)` : ''}`,
    ]);
  }
  if (items.length === 0) return null;
  return (
    <Card>
      <dl className="space-y-1 text-sm" data-testid="cash-bank-attention">
        {items.map(([label, value]) => (
          <div key={label} className="flex flex-wrap justify-between gap-x-3">
            <dt className="text-amber-800">{label}</dt>
            <dd className="whitespace-nowrap tabular-nums text-amber-800">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function Unassigned({ data }: { data: CashBankDashboard }): ReactNode {
  const m = (v: string) => money(v, data.currencyCode);
  if (data.unassigned.cash === '0.00' && data.unassigned.bank === '0.00') return null;
  return (
    <Card>
      <div className="space-y-2 text-sm" data-testid="unassigned">
        <h2 className="font-semibold text-amber-800">
          Not in an account: cash <span className="whitespace-nowrap">{m(data.unassigned.cash)}</span> · bank{' '}
          <span className="whitespace-nowrap">{m(data.unassigned.bank)}</span>
        </h2>
        <p className="text-xs text-slate-600">
          Recorded payments and expenses that named no account, at a location with no default account of that kind. They
          are real money movements, not missing deposits: add the cash and bank accounts for each location (or mark one as
          its default) and they land there — nothing is invented or posted twice.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pr-3">Kind</th>
                <th className="py-1 pr-3">Location</th>
                <th className="py-1 pr-3">Source</th>
                <th className="py-1 pr-3 text-right">Records</th>
                <th className="py-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.unassigned.bySource.map((u) => (
                <tr key={`${u.kind}-${u.branchName}-${u.source}`}>
                  <td className="py-1 pr-3">{u.kind === 'cash' ? 'Cash' : 'Bank'}</td>
                  <td className="py-1 pr-3">{u.branchName ?? 'No location'}</td>
                  <td className="py-1 pr-3">{SOURCE_LABELS[u.source] ?? u.source}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{u.count}</td>
                  <td className="whitespace-nowrap py-1 text-right tabular-nums">{m(u.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

/** One label and figure on a line, for the stacked layout on phones. */
function FigureLine({ label, value, tone, strong }: { label: string; value: string; tone?: string; strong?: boolean }): ReactNode {
  return (
    <div className={cn('flex justify-between gap-3', strong && 'border-t border-slate-100 pt-1.5 font-semibold text-slate-900')}>
      <dt className={strong ? undefined : 'text-slate-500'}>{label}</dt>
      <dd className={cn('whitespace-nowrap tabular-nums', tone ?? (strong ? undefined : 'text-slate-700'))}>{value}</dd>
    </div>
  );
}

/** A small square button named for screen readers and on hover, for a table row's actions. */
function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      title={label === 'Opening' ? 'Opening balance' : 'View movements'}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      {children}
    </button>
  );
}

function Accounts({
  accounts,
  openingAsOf,
  from,
  to,
  onView,
  onOpening,
}: {
  accounts: CashBankAccountView[];
  openingAsOf: string;
  from: string;
  to: string;
  onView: (a: CashBankAccountView) => void;
  onOpening?: ((a: MoneyAccountView) => void) | undefined;
}): ReactNode {
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        description="Add the cash held at each location or by each worker, and the bank accounts payments go into."
      />
    );
  }
  const range = from === to ? dayLabel(from) : `${dayLabel(from)} – ${dayLabel(to)}`;
  // Moving money between the company's own accounts gets a column only when some account did it.
  const showTransfers = accounts.some((a) => a.period && (a.period.transfersIn !== '0.00' || a.period.transfersOut !== '0.00'));
  const figureColumns = showTransfers ? 5 : 4;
  const head = (label: string, when: string) => (
    <th className="px-2.5 py-2.5 text-right font-medium">
      <span className="block text-slate-700">{label}</span>
      <span className="block whitespace-nowrap text-xs font-normal text-slate-500">{when}</span>
    </th>
  );
  return (
    <Card padded={false}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pb-3 pt-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Accounts</h2>
          <p className="text-sm text-slate-500">Each account&apos;s movements for the dates chosen, and who holds it.</p>
        </div>
        <p className="text-sm text-slate-500">{range}</p>
      </div>
      <ul className="divide-y divide-slate-100 border-t border-slate-200 sm:hidden" data-testid="cash-bank-accounts-mobile">
        {accounts.map((a) => (
          <li key={a.id} className={cn('px-5 py-4', a.archived && 'text-slate-400')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-1.5 font-medium text-slate-900">
                  {a.name} {a.isDefault ? <Badge tone="info">Default</Badge> : null}
                  {a.archived ? <Badge tone="neutral">Closed</Badge> : null}
                </p>
                <p className="text-xs text-slate-500">
                  {[MONEY_ACCOUNT_KIND_LABELS[a.kind], a.branchName ?? 'Whole business', a.holderName ? `held by ${a.holderName}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="flex shrink-0">
                <IconAction label="Movements" onClick={() => onView(a)}><History aria-hidden className="h-4 w-4" /></IconAction>
                {onOpening ? (
                  <IconAction label="Opening" onClick={() => onOpening(a)}><PencilLine aria-hidden className="h-4 w-4" /></IconAction>
                ) : null}
              </div>
            </div>
            {a.period === null ? (
              <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                <Lock aria-hidden className="h-3 w-3" />
                Full balance unavailable
              </p>
            ) : (
              <dl className="mt-2 space-y-1 text-sm">
                <FigureLine label={`Opening · ${dayLabel(openingAsOf)}`} value={money(a.period.opening, a.currencyCode)} />
                <FigureLine label="Money In" value={money(a.period.moneyIn, a.currencyCode)} tone="text-emerald-700" />
                <FigureLine label="Money Out" value={money(a.period.moneyOut, a.currencyCode)} tone="text-rose-700" />
                {showTransfers ? (
                  <FigureLine label="Own transfers" value={money(subtractAmounts(a.period.transfersIn, a.period.transfersOut), a.currencyCode)} />
                ) : null}
                <FigureLine label={`Closing · ${dayLabel(to)}`} value={money(a.balance, a.currencyCode)} strong />
                {a.period.difference !== '0.00' ? (
                  <p role="alert" className="text-xs text-rose-700">
                    {money(a.period.difference, a.currencyCode)} not explained by its movements
                  </p>
                ) : null}
              </dl>
            )}
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[920px] text-sm" data-testid="cash-bank-accounts">
          <thead className="border-y border-slate-200 bg-slate-50 text-left">
            <tr>
              <th className="py-2.5 pl-5 pr-3 font-medium text-slate-700">Account</th>
              <th className="px-3 py-2.5 font-medium text-slate-700">Location</th>
              {head('Opening', dayLabel(openingAsOf))}
              {head('Money In', 'in the period')}
              {head('Money Out', 'in the period')}
              {showTransfers ? head('Transfers', 'own accounts') : null}
              {head('Closing', dayLabel(to))}
              <th className="px-3 py-2.5 font-medium text-slate-700">Status</th>
              <th className="py-2.5 pr-5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {accounts.map((a) => (
              <tr key={a.id} className={a.archived ? 'text-slate-400' : 'hover:bg-slate-50/60'}>
                <td className="min-w-[11rem] py-3 pl-5 pr-3">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {a.kind === 'cash' ? (
                      <Banknote aria-hidden className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Landmark aria-hidden className="h-4 w-4 text-brand-600" />
                    )}
                    <span className="font-medium text-slate-900">{a.name}</span>
                    {a.isDefault ? <Badge tone="info">Default</Badge> : null}
                    {a.archived ? <Badge tone="neutral">Closed</Badge> : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {[MONEY_ACCOUNT_KIND_LABELS[a.kind], a.holderName ? `held by ${a.holderName}` : null].filter(Boolean).join(' · ')}
                  </span>
                  {!a.openingOn && !a.archived ? (
                    <span className="block text-xs text-amber-700">No opening date: counts every movement since records began</span>
                  ) : (
                    <span className="block text-xs text-slate-400">Opened {a.openingOn ? dayLabel(a.openingOn) : ''} with {money(a.openingBalance, a.currencyCode)}</span>
                  )}
                </td>
                <td className="px-3 py-3 text-slate-700">{a.branchName ?? 'Whole business'}</td>
                {a.period === null ? (
                  <td colSpan={figureColumns} className="px-2.5 py-3 text-right" title={BALANCE_UNAVAILABLE_WHY} data-testid="balance-unavailable">
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                      <Lock aria-hidden className="h-3.5 w-3.5" />
                      {BALANCE_UNAVAILABLE}
                    </span>
                  </td>
                ) : (
                  <>
                    <td className="whitespace-nowrap px-2.5 py-3 text-right tabular-nums text-slate-700">{money(a.period.opening, a.currencyCode)}</td>
                    <td className="whitespace-nowrap px-2.5 py-3 text-right tabular-nums text-emerald-700">{money(a.period.moneyIn, a.currencyCode)}</td>
                    <td className="whitespace-nowrap px-2.5 py-3 text-right tabular-nums text-rose-700">{money(a.period.moneyOut, a.currencyCode)}</td>
                    {showTransfers ? (
                      <td className="whitespace-nowrap px-2.5 py-3 text-right tabular-nums text-slate-700">
                        {money(subtractAmounts(a.period.transfersIn, a.period.transfersOut), a.currencyCode)}
                      </td>
                    ) : null}
                    <td className="whitespace-nowrap px-2.5 py-3 text-right font-semibold tabular-nums text-slate-900">
                      {money(a.balance, a.currencyCode)}
                      {a.period.difference !== '0.00' ? (
                        <span role="alert" className="block text-xs font-normal text-rose-700">
                          {money(a.period.difference, a.currencyCode)} not explained by its movements
                        </span>
                      ) : null}
                    </td>
                  </>
                )}
                <td className="whitespace-nowrap px-3 py-3" data-testid="balance-status">
                  {a.period === null ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                      <Lock aria-hidden className="h-3 w-3" />
                      Full balance unavailable
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                      <CheckCircle2 aria-hidden className="h-3 w-3" />
                      Complete balance
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap py-3 pr-4 text-right">
                  <IconAction label="Movements" onClick={() => onView(a)}><History aria-hidden className="h-4 w-4" /></IconAction>
                  {onOpening ? (
                    <IconAction label="Opening" onClick={() => onOpening(a)}><PencilLine aria-hidden className="h-4 w-4" /></IconAction>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 pb-4 pt-3 text-xs text-slate-500">
        Opening + money in − money out{showTransfers ? ' ± own transfers' : ''} = closing, for the dates chosen — never since
        records began.
      </p>
    </Card>
  );
}

function Transfers({
  transfers,
  currency,
  mayManage,
  today,
}: {
  transfers: MoneyTransferView[];
  currency: string;
  mayManage: boolean;
  today: string;
}): ReactNode {
  const voidTransfer = useVoidTransfer();
  const [voiding, setVoiding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  if (transfers.length === 0) return null;
  return (
    <section>
      <h2 className="mb-2.5 text-sm font-semibold text-slate-900">Handovers, deposits and transfers</h2>
      <Card>
        <ul className="divide-y divide-slate-100" data-testid="transfers">
          {transfers.map((t) => (
            <li key={t.id} className="py-2.5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className={t.voided ? 'text-slate-400 line-through' : undefined}>
                  <p className="text-sm font-medium">
                    <span className="whitespace-nowrap">{money(t.amount, currency)}</span> · {TRANSFER_KIND_LABEL[t.kind]} · {t.fromAccountName} → {t.toAccountName}{' '}
                    {!t.voided && t.status === 'pending' ? <Badge tone="warning">Waiting</Badge> : null}
                  </p>
                  <p className="text-xs text-slate-500">
                    Sent {t.transferredOn}
                    {t.completedOn ? ` · arrived ${t.completedOn}` : ''}
                    {t.submittedBy ? ` · handed over by ${t.submittedBy}` : ''}
                    {t.receiverName ? ` · to ${t.receiverName}` : ''}
                    {t.handledByName ? ` · handled by ${t.handledByName}` : ''}
                    {t.reference ? ` · ref ${t.reference}` : ''}
                    {t.completionReference ? ` · bank ref ${t.completionReference}` : ''}
                    {t.evidenceAttachmentId ? ' · receipt attached' : ''}
                    {t.recordedBy ? ` · recorded by ${t.recordedBy}` : ''}
                    {t.completedBy && t.completedBy !== t.recordedBy ? ` · confirmed by ${t.completedBy}` : ''}
                  </p>
                </div>
                {t.voided ? (
                  <span className="text-xs text-red-700">Voided: {t.voidReason}</span>
                ) : t.status === 'pending' ? (
                  <ConfirmOrDecline transfer={t} today={today} toBank={t.kind === 'deposit' || t.kind === 'transfer'} mayCancel={t.mayCancel} />
                ) : mayManage && voiding !== t.id ? (
                  <Button variant="ghost" size="sm" onClick={() => { setVoiding(t.id); setReason(''); }}>Void</Button>
                ) : null}
              </div>
              {voiding === t.id ? (
                <form
                  className="mt-2 flex items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    voidTransfer.mutate({ id: t.id, reason }, { onSuccess: () => setVoiding(null) });
                  }}
                >
                  <div className="flex-1">
                    <Input label="Why is it void?" value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setVoiding(null)}>Cancel</Button>
                  <Button type="submit" variant="danger" size="sm" loading={voidTransfer.isPending} disabled={reason.trim().length < 3}>
                    Void transfer
                  </Button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

function CreateAccountModal({ members, onClose }: { members: MemberOption[]; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const create = useCreateAccount();
  const { locations } = useWorkingLocation();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<MoneyAccountKind>('cash');
  const [branchId, setBranchId] = useState(locations[0]?.id ?? '');
  const [isDefault, setIsDefault] = useState(true);
  const [openingBalance, setOpeningBalance] = useState('');
  // Counted from today unless told otherwise: an account with no date takes in every movement ever recorded.
  const [openingOn, setOpeningOn] = useState(todayIn(business?.timezone ?? 'UTC'));
  const [holderUserId, setHolderUserId] = useState('');
  const [holderName, setHolderName] = useState('');

  return (
    <Modal open onClose={onClose} title="Add an account">
      <form
        className="space-y-4"
        aria-label="Add an account"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              name,
              kind,
              branchId: branchId || null,
              isDefault,
              ...(openingBalance ? { openingBalance } : {}),
              ...(openingOn ? { openingOn } : {}),
              ...(kind === 'cash' && holderUserId ? { holderUserId } : {}),
              ...(kind === 'cash' && !holderUserId && holderName.trim() ? { holderName: holderName.trim() } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Holy Ghost till, Chidi's float, Moniepoint, GTBank" />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as MoneyAccountKind)}
            options={MONEY_ACCOUNT_KINDS.map((k) => ({ value: k, label: MONEY_ACCOUNT_KIND_LABELS[k] }))}
          />
          <Select
            label="Location"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={[{ value: '', label: 'Whole business' }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
          />
          {kind === 'cash' ? (
            <>
              <Select
                label="Held by (worker)"
                value={holderUserId}
                onChange={(e) => setHolderUserId(e.target.value)}
                options={[{ value: '', label: 'Nobody in particular' }, ...members.map((mb) => ({ value: mb.userId, label: mb.fullName ?? mb.email ?? 'Member' }))]}
              />
              {!holderUserId ? (
                <Input label="Or holder's name" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Not a CenoX user" />
              ) : (
                <span />
              )}
            </>
          ) : null}
          <Input label="Opening balance" inputMode="decimal" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} hint="What it held before CenoX recorded it" />
          <Input
            label="Counted from"
            type="date"
            value={openingOn}
            onChange={(e) => setOpeningOn(e.target.value)}
            hint="The day the opening balance was true. Movements before it are inside the opening balance."
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Default for {kind === 'cash' ? 'cash' : 'non-cash'} payments at this location that name no account
        </label>
        {create.error ? (
          <p role="alert" className="text-sm text-red-700">{create.error instanceof ApiError ? create.error.message : 'Could not add the account.'}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending} disabled={name.trim().length === 0 || !openingOn}>Add account</Button>
        </div>
      </form>
    </Modal>
  );
}

function TransferModal({
  accounts,
  mayManage,
  toKind,
  onClose,
}: {
  accounts: MoneyAccountView[];
  mayManage: boolean;
  /** Start with a destination of this kind: cash for a handover, bank for a deposit. */
  toKind?: 'cash' | 'bank' | undefined;
  onClose: () => void;
}): ReactNode {
  const { business, user } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const record = useRecordTransfer();
  const upload = useUploadEvidence();
  const [key] = useState(newSubmissionKey);
  const [fromAccountId, setFrom] = useState(accounts.find((a) => a.kind === 'cash')?.id ?? accounts[0]?.id ?? '');
  const [toAccountId, setTo] = useState(
    (toKind === 'cash'
      ? accounts.find((a) => a.kind === 'cash' && a.id !== fromAccountId)
      : accounts.find((a) => a.kind !== 'cash'))?.id ??
      accounts.find((a) => a.id !== fromAccountId)?.id ??
      '',
  );
  const [amount, setAmount] = useState('');
  const [transferredOn, setOn] = useState(today);
  const [reference, setReference] = useState('');
  const [handledByName, setHandled] = useState('');
  const [arrived, setArrived] = useState(mayManage);
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const options = accounts.map((a) => ({ value: a.id, label: `${a.name} (${money(a.balance, a.currencyCode)})` }));
  const from = accounts.find((a) => a.id === fromAccountId);
  const to = accounts.find((a) => a.id === toAccountId);
  const isDeposit = from?.kind === 'cash' && to?.kind !== 'cash';
  // Cash into another person's hands waits for them to confirm it; nobody confirms it for them.
  const toSomeoneElse = from?.kind === 'cash' && to?.kind === 'cash' && Boolean(to.holderUserId) && to.holderUserId !== user?.id;
  // Recording a worker's handover for them: it is theirs, not the recorder's.
  const onBehalfOf = from?.holderUserId && from.holderUserId !== user?.id ? from.holderUserId : null;
  const [secondOne, setSecondOne] = useState(false);
  const duplicate = record.error instanceof ApiError && record.error.status === 409 && Boolean(record.error.fields?.confirmDuplicate);

  return (
    <Modal open onClose={onClose} title="Move money">
      <form
        className="space-y-4"
        aria-label="Move money"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          record.mutate(
            {
              fromAccountId,
              toAccountId,
              amount,
              transferredOn,
              idempotencyKey: key,
              pending: toSomeoneElse || !arrived,
              ...(onBehalfOf && mayManage ? { submittedByUserId: onBehalfOf } : {}),
              ...(secondOne ? { confirmDuplicate: true } : {}),
              ...(reference ? { reference } : {}),
              ...(handledByName.trim() ? { handledByName: handledByName.trim() } : {}),
              ...(evidenceId ? { evidenceAttachmentId: evidenceId } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className="text-sm text-slate-600">
          One account goes down and the other goes up by the same amount — a deposit, a withdrawal or a transfer. It is
          never revenue or an expense. Part of the cash may be banked; the rest stays where it is.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Select label="From" value={fromAccountId} onChange={(e) => setFrom(e.target.value)} options={options} />
          <Select label="To" value={toAccountId} onChange={(e) => setTo(e.target.value)} options={options} />
          <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Input label="Date sent" type="date" max={today} value={transferredOn} onChange={(e) => setOn(e.target.value)} />
          <Input label="Handled by" value={handledByName} onChange={(e) => setHandled(e.target.value)} placeholder="Who carried or sent it" />
          <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Deposit slip or transfer number" />
        </div>
        <div className="text-sm">
          <label className="block text-xs font-medium text-slate-600" htmlFor="move-money-evidence">Receipt or deposit slip</label>
          <input
            id="move-money-evidence"
            type="file"
            accept="image/*,application/pdf"
            className="mt-1 text-sm"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f, { onSuccess: (r) => setEvidenceId(r.id) });
            }}
          />
          {upload.isPending ? <p className="text-xs text-slate-500">Uploading…</p> : null}
          {evidenceId ? <p className="text-xs text-emerald-700">Receipt attached.</p> : null}
          {upload.error ? <p className="text-xs text-red-700">{upload.error.message}</p> : null}
        </div>
        {toSomeoneElse ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            A handover: it leaves “{from?.name}” now and is {to?.holderName ?? 'the receiver'}&apos;s once they confirm they received it.
          </p>
        ) : mayManage ? (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" checked={arrived} onChange={(e) => setArrived(e.target.checked)} />
            <span>
              It has arrived in “{to?.name ?? 'the destination'}”. Untick while {isDeposit ? 'the cash is on its way to the bank' : 'it is not confirmed yet'}:
              it leaves “{from?.name ?? 'the source'}” now and lands only when someone confirms it.
            </span>
          </label>
        ) : (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Your deposit stays pending until someone who reconciles cash confirms it arrived.
          </p>
        )}
        {duplicate ? (
          <label className="flex items-start gap-2 text-sm text-amber-900">
            <input type="checkbox" className="mt-1" checked={secondOne} onChange={(e) => setSecondOne(e.target.checked)} />
            <span>This is a second, separate amount — not the same money entered again.</span>
          </label>
        ) : null}
        {record.error ? (
          <p role="alert" className="text-sm text-red-700">{record.error instanceof ApiError ? record.error.message : 'Could not record it.'}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={record.isPending} disabled={!amount || !fromAccountId || fromAccountId === toAccountId || (duplicate && !secondOne)}>Move money</Button>
        </div>
      </form>
    </Modal>
  );
}

function MovementsModal({
  account,
  from,
  to,
  openingAsOf,
  onClose,
}: {
  account: CashBankAccountView;
  from: string;
  to: string;
  openingAsOf: string;
  onClose: () => void;
}): ReactNode {
  // Before the account's opening date its movements are inside the opening balance, so the list starts there.
  const start = account.openingOn && account.openingOn > from ? account.openingOn : from;
  const movements = useAccountMovements(account.id, { from: start, to });
  const m = (v: string) => money(v, account.currencyCode);
  const rows = movements.data?.movements ?? [];
  const complete = movements.data?.complete ?? true;
  const listed = rows.reduce((t, r) => addAmounts(t, r.amount), '0.00');
  // Not available to someone limited to some locations when the account holds money recorded at others.
  const figures = account.balance === null || account.period === null ? null : { opening: account.period.opening, closing: account.balance };
  const moved = figures ? subtractAmounts(figures.closing, figures.opening) : null;
  return (
    <Modal open onClose={onClose} title={`${account.name}: movements ${dayLabel(start)} to ${dayLabel(to)}`}>
      {movements.isPending ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-2" data-testid="account-history">
          <div className="flex justify-between gap-3 text-sm font-medium text-slate-700" data-testid="account-history-opening">
            <span>
              {start > from ? `Opening balance — recorded for ${dayLabel(start)}` : `Opening balance — as of ${dayLabel(openingAsOf)}`}
            </span>
            <span className="whitespace-nowrap tabular-nums">{figures ? m(figures.opening) : BALANCE_UNAVAILABLE}</span>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing moved through this account in these dates.</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {rows.map((mv, i) => (
                    <tr key={`${mv.source}-${mv.ref}-${i}`}>
                      <td className="py-1.5 pr-3 text-xs text-slate-500">{mv.on}</td>
                      <td className="py-1.5 pr-3">{SOURCE_LABELS[mv.source] ?? mv.source}</td>
                      <td className={`whitespace-nowrap py-1.5 text-right tabular-nums ${Number(mv.amount) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {m(mv.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-between gap-3 border-t border-slate-200 pt-2 text-sm font-semibold text-slate-900" data-testid="account-history-closing">
            <span>Closing balance — as of {dayLabel(to)}</span>
            <span className="whitespace-nowrap tabular-nums">{figures ? m(figures.closing) : BALANCE_UNAVAILABLE}</span>
          </div>
          {!figures ? (
            <p className="text-xs text-slate-500" data-testid="account-history-unavailable">
              {BALANCE_UNAVAILABLE_WHY} The movements listed are those recorded at your locations.
            </p>
          ) : !complete ? (
            <p role="alert" className="text-xs text-amber-800" data-testid="account-history-incomplete">
              These dates hold more movements than can be listed at once, so the list above is incomplete and does not add up
              to the change in the balance. The opening and closing balances are accurate: they are worked out from every
              movement. Choose shorter dates to see each one.
            </p>
          ) : moved !== null && listed !== moved ? (
            <p role="alert" className="text-xs text-rose-700" data-testid="account-history-difference">
              The movements listed add up to {m(listed)}, but the balance moved by {m(moved)}. Please report this so it can be
              looked into.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/**
 * What an account held when CenoX started counting it, from the bank
 * statement or a cash count, and from which day. Movements before that day are
 * inside it. Changing it moves the balance on every day since, so it asks why.
 */
function OpeningModal({ account, today, onClose }: { account: MoneyAccountView; today: string; onClose: () => void }): ReactNode {
  const update = useUpdateAccount();
  // An account with no date was never given a balance: it is typed from the statement, never assumed to be 0.
  const [openingBalance, setBalance] = useState(account.openingOn ? account.openingBalance : '');
  const [openingOn, setOn] = useState(account.openingOn ?? today);
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title={`${account.name}: opening balance`}>
      <form
        className="space-y-4"
        aria-label="Opening balance"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate({ id: account.id, openingBalance, openingOn, reason: reason.trim() }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          {account.kind === 'cash'
            ? 'What was physically counted in this cash at the start of the day.'
            : 'The balance on the bank statement at the start of the day.'}{' '}
          Money recorded before that day is inside this figure and is not counted again; money recorded from that day on is
          added to it. Nothing is booked: only where the account starts counting changes.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Opening balance" inputMode="decimal" value={openingBalance} onChange={(e) => setBalance(e.target.value)} />
          <Input label="Counted from" type="date" max={today} value={openingOn} onChange={(e) => setOn(e.target.value)} />
        </div>
        <Input label="Why, and from what" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="GTBank statement of 26 Oct, page 1" />
        {update.error ? <p role="alert" className="text-sm text-red-700">{update.error.message}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={update.isPending} disabled={!openingBalance || !openingOn || reason.trim().length < 3}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
